import React, { useCallback, useEffect, useMemo, useState } from "react";
import oraclePhase27Api from "@/api/oraclePhase27Client";
import ComplianceLock from "@/components/compliance/ComplianceLock";

/**
 * CareLogix™ ORACLE — Scanner Panel (Phase 27D).
 *
 * 12-category CareLogix-native scanner. Public preview with full table.
 * Used standalone and inside Market Predictor Hub.
 */

const DIR_COLORS = { bullish: "#10b981", neutral: "#c5a059", bearish: "#ef4444" };

export default function OracleScannerPanel({
  defaultType = "premarket_movers",
  limit = 12,
  compact = false,
  testid = "oracle-scanner-panel",
}) {
  const [categories, setCategories] = useState([]);
  const [type, setType] = useState(defaultType);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    oraclePhase27Api.scannerCategories()
      .then(({ data }) => { if (alive) setCategories(data.categories || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const load = useCallback(async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await oraclePhase27Api.scanner(type, limit);
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "scanner_failed");
    } finally { setBusy(false); }
  }, [type, limit]);

  useEffect(() => { load(); }, [load]);

  const items = data?.items || [];
  const meta = useMemo(() => categories.find((c) => c.id === type) || { label: type, icon: "⚡" }, [categories, type]);

  return (
    <section style={S.shell} data-testid={testid}>
      <header style={S.head}>
        <div style={S.headLeft}>
          <span style={S.tagline}>ORACLE Scanner™ · 12 modes</span>
          <h3 style={S.title}>{meta.icon} {meta.label}</h3>
        </div>
        <button type="button" onClick={load} disabled={busy} style={S.refresh} data-testid={`${testid}-refresh`}>
          {busy ? "…" : "↻ Refresh"}
        </button>
      </header>

      {!compact && (
        <div style={S.chips} role="tablist" aria-label="Scanner categories">
          {categories.map((c) => {
            const on = c.id === type;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => setType(c.id)}
                role="tab"
                aria-selected={on}
                data-testid={`scanner-cat-${c.id}`}
                style={{
                  ...S.chip,
                  color: on ? "#c5a059" : "#475569",
                  borderColor: on ? "#c5a059" : "rgba(255,255,255,0.10)",
                  background: on ? "rgba(197,160,89,0.10)" : "transparent",
                }}
              >
                <span style={{ marginRight: 4 }}>{c.icon}</span>
                {c.label}
              </button>
            );
          })}
        </div>
      )}

      {err && <div style={S.err} data-testid={`${testid}-error`}>⚠ {err}</div>}

      <div style={S.tableScroll}>
        <div style={S.tableHead}>
          <span style={S.colSym}>SYMBOL</span>
          <span style={S.colDir}>DIR</span>
          <span style={S.colNum}>OPP</span>
          <span style={S.colNum}>CONF</span>
          <span style={S.colNum}>RISK</span>
          <span style={S.colNum}>24H</span>
          <span style={S.colNum}>VOL×</span>
          <span style={S.colReason}>WHY</span>
        </div>
        {items.length === 0 ? (
          <p style={S.empty}>{busy ? "Scanning…" : "No matches."}</p>
        ) : (
          <div style={S.tableBody}>
            {items.map((row, i) => {
              const dirColor = DIR_COLORS[row.direction] || DIR_COLORS.neutral;
              return (
                <div key={`${row.symbol}-${i}`} style={S.row} data-testid={`scanner-row-${i}`}>
                  <span style={{ ...S.colSym, color: "#e2e8f0", fontFamily: "'Space Mono', monospace" }}>{row.symbol}</span>
                  <span style={{ ...S.colDir, color: dirColor, fontWeight: 800, fontFamily: "'Space Mono', monospace" }}>{row.direction.toUpperCase()}</span>
                  <span style={{ ...S.colNum, color: "#c5a059", fontFamily: "'Space Mono', monospace", fontWeight: 800 }}>{row.opportunity_score}</span>
                  <span style={{ ...S.colNum, color: "#10b981", fontFamily: "'Space Mono', monospace" }}>{row.confidence_score}</span>
                  <span style={{ ...S.colNum, color: "#fca5a5", fontFamily: "'Space Mono', monospace" }}>{row.risk_score}</span>
                  <span style={{ ...S.colNum, color: row.pct_change_24h >= 0 ? "#10b981" : "#ef4444", fontFamily: "'Space Mono', monospace" }}>
                    {row.pct_change_24h >= 0 ? "+" : ""}{row.pct_change_24h}%
                  </span>
                  <span style={{ ...S.colNum, color: "#cbd5e1", fontFamily: "'Space Mono', monospace" }}>{row.volume_x_avg}×</span>
                  <span style={S.colReason}>{row.reason}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ComplianceLock variant="inline" short policy="v27" testid={`${testid}-compliance`} />
    </section>
  );
}

const S = {
  shell: { background: "rgba(11,15,20,0.94)", border: "1px solid rgba(197,160,89,0.20)", borderRadius: 14, padding: 14, color: "#e2e8f0", display: "flex", flexDirection: "column", gap: 10, fontFamily: "'Figtree', 'Inter', system-ui, sans-serif", maxWidth: "100%", minWidth: 0, boxSizing: "border-box" },
  head: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" },
  headLeft: { display: "flex", flexDirection: "column", gap: 2 },
  tagline: { color: "#c5a059", fontFamily: "'Space Mono', monospace", fontSize: 10.5, letterSpacing: "0.18em" },
  title: { color: "#fde68a", fontSize: 17, margin: 0 },
  refresh: { appearance: "none", background: "linear-gradient(135deg, #c5a059, #ffd966)", border: "1px solid #c5a059", color: "#0c1a10", padding: "6px 12px", borderRadius: 8, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 800, letterSpacing: "0.06em" },
  chips: { display: "flex", flexWrap: "wrap", gap: 6 },
  chip: { appearance: "none", padding: "5px 10px", borderRadius: 999, border: "1px solid", cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 10.5, letterSpacing: "0.06em", fontWeight: 800 },
  err: { padding: "8px 12px", background: "rgba(239,68,68,0.10)", border: "1px solid rgba(239,68,68,0.30)", color: "#fecaca", borderRadius: 10, fontSize: 12 },
  tableScroll: { width: "100%", maxWidth: "100%", overflowX: "auto", border: "1px solid rgba(255,255,255,0.04)", borderRadius: 10 },
  tableHead: { display: "grid", gridTemplateColumns: "110px 70px 60px 60px 60px 70px 60px minmax(220px, 1fr)", gap: 6, padding: "6px 10px", background: "rgba(0,0,0,0.30)", fontFamily: "'Space Mono', monospace", fontSize: 9.5, letterSpacing: "0.12em", color: "#64748b", minWidth: 720 },
  tableBody: { display: "flex", flexDirection: "column", gap: 4, maxHeight: 420, overflowY: "auto", minWidth: 720 },
  row: { display: "grid", gridTemplateColumns: "110px 70px 60px 60px 60px 70px 60px minmax(220px, 1fr)", gap: 6, padding: "6px 10px", background: "#040608", border: "1px solid rgba(255,255,255,0.04)", borderRadius: 8, alignItems: "center", fontSize: 11.5, minWidth: 720 },
  colSym: { fontSize: 12 },
  colDir: { fontSize: 10.5, letterSpacing: "0.06em" },
  colNum: { fontSize: 11.5, textAlign: "right" },
  colReason: { color: "#cbd5e1", fontStyle: "italic", fontSize: 11, lineHeight: 1.4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  empty: { textAlign: "center", padding: 12, color: "#94a3b8", fontFamily: "'Space Mono', monospace", fontSize: 12 },
};
